<template>
  <div class="margin">

    <!-- Hero -->
    <section class="margin__hero">
      <div class="margin__shell">
        <h1 class="margin__hero-title">{{ $t('blog.blog4.marginSummer') }}</h1>
        <div class="margin__hero-img-wrap">
          <img
            :src="$app_image + 'image_mua_he_ruc_ro.png'"
            :alt="$t('blog.blog4.summerGarish')"
            class="margin__hero-img"
          />
        </div>
        <h2 class="margin__hero-rate">
          {{ $t('blog.blog4.interestRate') }}
          <span>6.8%</span>
        </h2>
        <p class="margin__hero-sub">{{ $t('blog.blog4.product') }}</p>
        <button class="margin__hero-btn" @click="goToContact">
          <i class="fas fa-rocket"></i>
          {{ $t('common.contactNow') }}
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </section>

    <main class="margin__main">
      <div class="margin__shell">

        <!-- Interest rate cards -->
        <section class="margin__section">
          <h3 class="margin__section-title">{{ $t('blog.blog4.levelInterest') }}</h3>
          <div class="margin__rate-grid">
            <div
              v-for="(rate, index) in interestRates"
              :key="index"
              :class="['margin__rate-card', rate.isHighlighted ? 'margin__rate-card--active' : '']"
              @click="selectRate(index)"
            >
              <div class="margin__rate-value">{{ rate.rate }}</div>
              <div class="margin__rate-level">{{ rate.level }}</div>
              <div class="margin__rate-row">
                <span>{{ $t('blog.blog4.rotation') }}</span>
                <span>{{ rate.turnover }}</span>
              </div>
              <div class="margin__rate-bar">
                <div class="margin__rate-bar-fill" :style="{ width: rate.percentage + '%' }"></div>
              </div>
            </div>
          </div>
        </section>

        <!-- Features -->
        <section class="margin__section">
          <h3 class="margin__section-title">{{ $t('blog.blog4.feature') }}</h3>
          <div class="margin__feature-grid">
            <div
              v-for="(feature, index) in features"
              :key="index"
              class="margin__feature-card"
            >
              <div class="margin__feature-icon">
                <i :class="feature.icon"></i>
              </div>
              <h4>{{ feature.title }}</h4>
              <p>{{ feature.description }}</p>
            </div>
          </div>
        </section>

        <!-- Key info -->
        <section class="margin__section">
          <div class="margin__info-box">
            <div class="margin__info-header">
              <h3 class="margin__info-title">✨ {{ $t('blog.blog4.importantInfo') }}</h3>
              <div class="margin__info-divider"></div>
            </div>
            <div class="margin__info-grid">
              <div class="margin__info-item">
                <div class="margin__info-icon"><i class="fas fa-users"></i></div>
                <div>
                  <h4>{{ $t('blog.blog4.apply') }}</h4>
                  <p>{{ $t('blog.blog4.customer') }}</p>
                </div>
              </div>
              <div class="margin__info-item">
                <div class="margin__info-icon"><i class="fas fa-percentage"></i></div>
                <div>
                  <h4>{{ $t('blog.blog4.fees') }}</h4>
                  <p><strong>0.2%</strong> {{ $t('blog.blog4.total') }}</p>
                </div>
              </div>
              <div class="margin__info-item">
                <div class="margin__info-icon"><i class="fas fa-gift"></i></div>
                <div>
                  <h4>{{ $t('blog.blog4.endow') }}</h4>
                  <p><strong class="margin__green">{{ $t('blog.blog4.free') }}</strong> {{ $t('blog.blog4.buy') }}</p>
                </div>
              </div>
              <div class="margin__info-item">
                <div class="margin__info-icon"><i class="fas fa-calculator"></i></div>
                <div>
                  <h4>{{ $t('blog.blog4.solution') }}</h4>
                  <p><strong>{{ $t('blog.blog4.s') }}</strong></p>
                </div>
              </div>
              <div class="margin__info-item">
                <div class="margin__info-icon"><i class="fas fa-money-bill-wave"></i></div>
                <div>
                  <h4>{{ $t('blog.blog4.all') }}</h4>
                  <p><strong class="margin__red">{{ $t('blog.blog4.ty') }}</strong> {{ $t('blog.blog4.allProduct') }}</p>
                </div>
              </div>
              <div class="margin__info-item">
                <div class="margin__info-icon"><i class="fas fa-clock"></i></div>
                <div>
                  <h4>{{ $t('blog.blog4.time') }}</h4>
                  <p><strong class="margin__orange">{{ $t('blog.blog4.after') }}</strong> {{ $t('blog.blog4.since') }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Registration methods -->
        <section class="margin__section">
          <div class="margin__reg-box">
            <h3 class="margin__section-title">
              <i class="fas fa-clipboard-check"></i>
              {{ $t('blog.blog4.way') }}
            </h3>
            <div class="margin__reg-grid">
              <div class="margin__reg-card">
                <div class="margin__reg-icon margin__reg-icon--green">
                  <i class="fas fa-user-tie"></i>
                </div>
                <h4>{{ $t('blog.blog4.nvmg') }}</h4>
                <p>{{ $t('blog.blog4.contactNvmg') }}</p>
                <span class="margin__reg-badge margin__reg-badge--green">{{ $t('blog.blog4.advise') }}</span>
              </div>
              <div class="margin__reg-card">
                <div class="margin__reg-icon margin__reg-icon--purple">
                  <i class="fas fa-building"></i>
                </div>
                <h4>{{ $t('blog.blog4.live') }}</h4>
                <p>{{ $t('blog.blog4.regist') }}</p>
                <span class="margin__reg-badge margin__reg-badge--purple">{{ $t('blog.blog4.support') }}</span>
              </div>
              <div class="margin__reg-card">
                <div class="margin__reg-icon margin__reg-icon--gold">
                  <i class="fas fa-phone"></i>
                </div>
                <h4>{{ $t('common.hotline') }}</h4>
                <p>{{ $t('blog.blog4.call') }} <strong>{{ $t('tel') }}</strong></p>
                <span class="margin__reg-badge margin__reg-badge--gold">{{ $t('blog.blog4.fast') }}</span>
              </div>
            </div>
            <p class="margin__reg-note">{{ $t('blog.blog4.choose') }}</p>
          </div>
        </section>

        <!-- FAQ -->
        <section class="margin__section">
          <h3 class="margin__section-title">{{ $t('contact.question') }}</h3>
          <div class="margin__faq">
            <div
              v-for="(faq, index) in faqs"
              :key="index"
              :class="['margin__faq-item', faq.isOpen ? 'margin__faq-item--open' : '']"
              @click="toggleFAQ(index)"
            >
              <div class="margin__faq-question">
                <span>{{ faq.question }}</span>
                <i :class="['fas fa-chevron-down margin__faq-chevron', faq.isOpen ? 'margin__faq-chevron--open' : '']"></i>
              </div>
              <div v-if="faq.isOpen" class="margin__faq-answer">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>

<script src="./margin-summer-2025.js"></script>
<style src="./margin-summer-2025.scss" lang="scss"></style>
